import React, { useEffect, useId, useRef } from "react";
import { animate, stagger, svg, prefersReducedMotion } from "../../lib/anime";
import { usePreloader } from "../../context/PreloaderContext";
import { useSection } from "../../context/SectionContext";

/**
 * Survey lines over the field.
 *
 * Long-radius curves, a dashed sight line and a construction grid — the layer
 * that says the field is measured rather than decorative. The curves draw
 * themselves once the load screen clears, then re-survey (morph) as the reader
 * moves between chapters.
 *
 * anime.js territory: createDrawable for the draw-on, morphTo for the re-survey.
 * Neither is scroll-linked, so this never contends with GSAP.
 */

/* viewBox is 1440 x 900, sliced to cover — curves crop rather than distort. */
/* Routed to stay clear of the type: down the left edge, along the floor, then
   out to the right. The curve frames the copy rather than crossing it. */
const ARC = {
  home:    "M 62 -24 C 4 300, 18 706, 250 852 C 520 1024, 1186 636, 1470 424",
  work:    "M 20 -24 C -30 360, 120 740, 400 880 C 700 1030, 1230 700, 1470 540",
  contact: "M 120 -24 C 60 260, 30 690, 300 830 C 620 998, 1272 520, 1470 300",
};

const SWEEP = {
  home:    "M 1118 -16 C 1228 88, 1350 188, 1462 268",
  work:    "M 1042 -16 C 1188 132, 1330 236, 1462 340",
  contact: "M 1186 -16 C 1268 64, 1372 146, 1462 214",
};

const keyFor = (chapterId) => {
  if (chapterId === "contact") return "contact";
  if (chapterId === "home") return "home";
  return "work";
};

const Guides = ({ className = "field-layer", style }) => {
  const arcRef = useRef(null);
  const sweepRef = useRef(null);
  const scope = useRef(null);
  const drawn = useRef(false);
  const shape = useRef("home");
  const { isPreloaderDone } = usePreloader();
  const { activeId } = useSection();
  /* Two instances share the page, so the morph targets need unique ids. */
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, "");

  /* Draw-on, once, after the load screen clears */
  useEffect(() => {
    if (!isPreloaderDone || drawn.current || !scope.current) return;
    drawn.current = true;

    if (prefersReducedMotion()) return;

    const lines = svg.createDrawable(scope.current.querySelectorAll("[data-guide]"));
    animate(lines, {
      draw: ["0 0", "0 1"],
      duration: 2400,
      delay: stagger(140),
      ease: "inOutQuad",
    });
  }, [isPreloaderDone]);

  /* Re-survey on chapter change */
  useEffect(() => {
    if (!drawn.current) return;
    const next = keyFor(activeId);
    if (next === shape.current) return;
    shape.current = next;

    const arc = arcRef.current;
    const sweep = sweepRef.current;
    if (!arc || !sweep) return;

    if (prefersReducedMotion()) {
      arc.setAttribute("d", ARC[next]);
      sweep.setAttribute("d", SWEEP[next]);
      return;
    }

    animate(arc, { d: svg.morphTo(`#arc-${uid}-${next}`), duration: 1600, ease: "inOutQuart" });
    animate(sweep, { d: svg.morphTo(`#sweep-${uid}-${next}`), duration: 1600, ease: "inOutQuart" });
  }, [activeId, uid]);

  return (
    <svg
      ref={scope}
      className={className}
      style={style}
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {Object.entries(ARC).map(([key, d]) => (
          <path key={key} id={`arc-${uid}-${key}`} d={d} />
        ))}
        {Object.entries(SWEEP).map(([key, d]) => (
          <path key={key} id={`sweep-${uid}-${key}`} d={d} />
        ))}
      </defs>

      <g fill="none" vectorEffect="non-scaling-stroke">
        {/* Construction grid — the sheet the curves are drawn on */}
        <path
          data-guide
          d="M 0 292 L 1440 292"
          stroke="var(--line)"
          strokeWidth="1"
        />
        <path
          data-guide
          d="M 1010 0 L 1010 900"
          stroke="var(--line)"
          strokeWidth="1"
        />

        {/* Sight line — dashed, the way a survey drawing marks a projection */}
        <path
          ref={sweepRef}
          data-guide
          d={SWEEP.home}
          stroke="var(--line-strong)"
          strokeWidth="1"
          strokeDasharray="5 8"
        />

        {/* The long arc */}
        <path
          ref={arcRef}
          data-guide
          d={ARC.home}
          stroke="var(--line-strong)"
          strokeWidth="1.25"
        />
      </g>
    </svg>
  );
};

export default Guides;
